import { Controller, useFormContext } from "react-hook-form";
import { TextField } from "~/common/mui/TextField/TextField";
import { useTranslation } from "react-i18next";
import type { MediaFilterData, MediaFormFields } from "~/features/mediaLibrary/types/mediaLibraryFormTypes";
import { memo, useEffect, useMemo } from "react";
import { type SxProps, type Theme, Box, useTheme } from "~/common/mui";
import { debounce } from "lodash";
import SortByFilter from "../SortByFilter/SortByFilter";
import MediaLibraryFilters from "./MediaLibraryFilters";
import { trackMediaLibraryFilterActions } from "~/app/analytics/ga/mediaLibrary";
import SearchIcon from "@mui/icons-material/Search";

export const mediaLibrarySearchFieldStyles: SxProps<Theme> = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      border: "none",
    },
    "&:hover fieldset": {
      border: "none",
    },
    "&.Mui-focused fieldset": {
      border: "none",
    },
  },
};

const MediaLibrarySearchBox = memo(function MediaLibrarySearchBox(props: {
  filterOptions: MediaFilterData;
  submitForm: (data: MediaFormFields) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { submitForm, filterOptions } = props;
  const theme = useTheme();

  const { control, handleSubmit, subscribe } = useFormContext<MediaFormFields>();

  const debouncedSubmit = useMemo(() => debounce(() => handleSubmit(submitForm)(), 500), [handleSubmit, submitForm]);

  useEffect(() => {
    const subscriptions = [
      "mediaTypes",
      "productPortfolios",
      "languages",
      "segments",
      "fetchFavourite",
      "sortOrder",
    ].map((name) =>
      subscribe({
        name: [name as keyof MediaFormFields],
        formState: { values: true },
        callback: ({ values }) => {
          const key = name as keyof MediaFormFields;
          trackMediaLibraryFilterActions(values?.q, key, values[key] as string);
          debouncedSubmit.cancel();
          handleSubmit(submitForm)();
        },
      }),
    );
    return () => {
      subscriptions.forEach((subscription) => subscription());
    };
  }, [subscribe, debouncedSubmit, handleSubmit, submitForm]);

  return (
    <form
      onSubmit={handleSubmit((data) => {
        debouncedSubmit.cancel();
        const el = document.activeElement;
        if (el && "blur" in el) {
          (el as HTMLElement).blur();
        }
        submitForm(data);
      })}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          [theme.breakpoints.down("tablet")]: {
            flexDirection: "column",
            alignItems: "flex-start",
          },
        }}
      >
        <button type="submit" hidden />
        <Controller
          name="q"
          control={control}
          render={({ field: { value, onChange } }) => (
            <TextField
              placeholder={t("generic.button.search")}
              sx={[
                mediaLibrarySearchFieldStyles,
                {
                  width: "708px",
                  bgcolor: "surface.main",
                  borderRadius: 3.5,
                  mt: 3,
                  [theme.breakpoints.down("tablet")]: { width: 1 },
                },
              ]}
              slotProps={{
                input: {
                  startAdornment: (
                    <Box sx={{ mx: 0.5 }}>
                      <SearchIcon  />
                    </Box>
                  ),
                },
              }}
              value={value}
              onChange={(e) => {
                onChange(e);
                debouncedSubmit();
              }}
            />
          )}
        />
        <SortByFilter />
      </Box>
      <MediaLibraryFilters filterOptions={filterOptions} />
    </form>
  );
});

export default MediaLibrarySearchBox;
